import { useState } from "react";
import { Loader2, Send } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@openup/shared/primitives/ui/button";
import Spinner from "@openup/shared/components/Spinner/Spinner";
import { Textarea } from "@openup/shared/primitives/ui/textarea";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@openup/shared/primitives/ui/table";
import { InviteStatus } from "@src/models/spaces";
import { Badge } from "@openup/shared/primitives/ui/badge";
import { isValidEmail, parseEmails } from "@src/utils/email";
import { useCreateInvitesMutation, useInvites } from "@src/queries/events";

// Colour classes layered over the shared Badge primitive (its base provides
// the pill shape); keeps the prior per-status palette, incl. the neutral grey.
const STATUS_STYLE: Record<InviteStatus, string> = {
  sent: "bg-orange-100 text-indigo-800",
  opened: "bg-gray-100 text-gray-700",
  registered: "bg-green-700 text-white",
  bounced: "bg-indigo-950 text-white",
  cancelled: "bg-gray-100 text-gray-700",
};

interface EmailInvitesProps {
  /** The session (Space) these invites are for — invites are per-session. */
  sessionId: string;
}

/** Option A — OpenUp sends a personal invite email per address (Customer.io). */
const EmailInvites = ({ sessionId }: EmailInvitesProps) => {
  const invitesQuery = useInvites(sessionId);
  const createInvites = useCreateInvitesMutation(sessionId);
  const [raw, setRaw] = useState("");

  const invites = invitesQuery.data ?? [];

  const onSend = () => {
    const emails = parseEmails(raw);
    if (emails.length === 0) {
      toast.error("Paste at least one email address.");
      return;
    }
    const invalid = emails.filter((email) => !isValidEmail(email));
    if (invalid.length > 0) {
      toast.error(`Check these addresses: ${invalid.join(", ")}`);
      return;
    }
    // Never re-send to someone already invited to THIS session — only send to
    // addresses without an existing invite. (The backend's unique
    // (SpaceId, InviteeEmail) index is the hard guard; this gives accurate UX.)
    const alreadyInvited = new Set(invites.map((i) => i.email.toLowerCase()));
    const newEmails = emails.filter((email) => !alreadyInvited.has(email));
    const skipped = emails.length - newEmails.length;

    if (newEmails.length === 0) {
      toast.info("Everyone you entered has already been invited.");
      return;
    }

    createInvites.mutate(newEmails, {
      onSuccess: () => {
        const sent = `Invited ${newEmails.length} ${newEmails.length === 1 ? "person" : "people"}`;
        toast.success(
          skipped > 0
            ? `${sent}. ${skipped} already invited (skipped).`
            : `${sent}.`,
        );
        setRaw("");
      },
      onError: () => toast.error("Couldn't send invites. Please try again."),
    });
  };

  return (
    <div className="flex flex-col gap-4">
      <p className="text-sm text-gray-600">
        Paste employee emails (comma, space, or newline separated). OpenUp sends
        each person a personal invite link to this session.
      </p>
      <Textarea
        rows={4}
        value={raw}
        placeholder="anna@company.com, ben@company.com"
        onChange={(e) => setRaw(e.target.value)}
      />
      <div className="flex justify-end">
        <Button
          variant="default"
          onClick={onSend}
          disabled={createInvites.isPending}
        >
          {createInvites.isPending ? (
            <Loader2 className="mr-1 h-4 w-4 animate-spin" aria-hidden />
          ) : (
            <Send className="mr-1 h-4 w-4" aria-hidden />
          )}
          Send invites
        </Button>
      </div>

      {invitesQuery.isLoading && (
        <div className="flex justify-center py-6">
          <Spinner />
        </div>
      )}

      {invites.length > 0 && (
        <div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Email</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {invites.map((invite) => (
                <TableRow key={invite.id}>
                  <TableCell>{invite.email}</TableCell>
                  <TableCell>
                    <Badge className={STATUS_STYLE[invite.status]}>
                      {invite.status}
                    </Badge>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
};

export default EmailInvites;
